const drawerState = new Map();

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

function getDrawerParts(drawerId) {
  const panel = document.getElementById(drawerId);
  if (!panel) return null;

  const root = panel.closest('[data-drawer-root]') || panel.closest('.drawer');
  const checkbox = root?.querySelector('.drawer-toggle') || null;
  const overlay = root?.querySelector(`[data-drawer-overlay="${drawerId}"]`) || null;
  const triggers = Array.from(
    document.querySelectorAll(`[data-drawer-trigger="${drawerId}"]`),
  );

  return { drawerId, root, panel, checkbox, overlay, triggers };
}

function getFocusableElements(panel) {
  return Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
    if (element.hidden) return false;
    if (element.getAttribute('aria-hidden') === 'true') return false;
    if (element.closest('[hidden]')) return false;
    return true;
  });
}

function syncExpanded(triggers, expanded) {
  triggers.forEach((trigger) => {
    trigger.setAttribute('aria-expanded', String(expanded));
  });
}

function focusFirst(panel) {
  const autofocused = panel.querySelector('[autofocus]');
  const focusables = getFocusableElements(panel);
  const target = autofocused || focusables[0] || panel;
  if (!panel.hasAttribute('tabindex')) {
    panel.setAttribute('tabindex', '-1');
  }
  target.focus();
}

function handleTabKey(event, panel) {
  if (event.key !== 'Tab') return;

  const focusables = getFocusableElements(panel);
  if (!focusables.length) {
    event.preventDefault();
    panel.focus();
    return;
  }

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;

  if (event.shiftKey && (active === first || active === panel)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

function attachPanelHandlers(parts) {
  const { drawerId, panel, overlay, checkbox } = parts;
  if (!panel || panel.dataset.drawerA11yBound === 'true') return;

  panel.addEventListener('keydown', (event) => {
    if (!isDrawerOpen(drawerId)) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawerById(drawerId);
      return;
    }

    handleTabKey(event, panel);
  });

  overlay?.addEventListener('click', () => {
    closeDrawerById(drawerId);
  });

  panel.addEventListener('click', (event) => {
    const closeButton = event.target.closest(`[data-drawer-close="${drawerId}"]`);
    if (closeButton) {
      closeDrawerById(drawerId);
    }
  });

  checkbox?.addEventListener('change', () => {
    if (checkbox.checked) {
      openDrawerById(drawerId);
    } else if (isDrawerOpen(drawerId)) {
      closeDrawerById(drawerId);
    }
  });

  panel.dataset.drawerA11yBound = 'true';
}

function bindTrigger(trigger) {
  if (trigger.dataset.drawerA11yBound === 'true') return;

  const drawerId = trigger.dataset.drawerTrigger;
  const parts = getDrawerParts(drawerId);
  if (!parts) return;

  attachPanelHandlers(parts);

  trigger.addEventListener('click', () => {
    if (isDrawerOpen(drawerId)) {
      closeDrawerById(drawerId);
    } else {
      openDrawerById(drawerId, trigger);
    }
  });

  trigger.dataset.drawerA11yBound = 'true';
}

export function isDrawerOpen(drawerId) {
  return drawerState.get(drawerId)?.open === true;
}

export function openDrawerById(drawerId, triggerOverride = null) {
  const parts = getDrawerParts(drawerId);
  if (!parts?.panel || !parts.root) return;

  attachPanelHandlers(parts);

  const activeTrigger = triggerOverride || document.activeElement;
  const returnFocusTo =
    activeTrigger instanceof HTMLElement && parts.triggers.includes(activeTrigger)
      ? activeTrigger
      : drawerState.get(drawerId)?.trigger || parts.triggers[0] || null;

  parts.root.dataset.drawerOpen = 'true';
  parts.panel.hidden = false;
  parts.overlay?.removeAttribute('hidden');

  if (parts.checkbox) {
    if (!parts.checkbox.checked) {
      parts.checkbox.checked = true;
    }
    parts.checkbox.setAttribute('aria-hidden', 'true');
    parts.checkbox.tabIndex = -1;
  }

  syncExpanded(parts.triggers, true);
  drawerState.set(drawerId, { open: true, trigger: returnFocusTo });

  requestAnimationFrame(() => {
    focusFirst(parts.panel);
  });
}

export function closeDrawerById(drawerId) {
  const parts = getDrawerParts(drawerId);
  if (!parts?.panel || !parts.root) return;

  const state = drawerState.get(drawerId);

  parts.root.dataset.drawerOpen = 'false';
  parts.panel.hidden = true;
  parts.overlay?.setAttribute('hidden', 'hidden');

  if (parts.checkbox) {
    parts.checkbox.checked = false;
  }

  syncExpanded(parts.triggers, false);
  drawerState.set(drawerId, { open: false, trigger: state?.trigger || null });

  if (state?.trigger instanceof HTMLElement) {
    requestAnimationFrame(() => {
      state.trigger.focus();
    });
  }
}

export function initDrawers(root = document) {
  root.querySelectorAll('[data-drawer-trigger]').forEach(bindTrigger);

  root.querySelectorAll('[data-drawer-panel]').forEach((panel) => {
    const parts = getDrawerParts(panel.id);
    if (!parts) return;

    parts.panel.hidden = true;
    parts.overlay?.setAttribute('hidden', 'hidden');
    parts.root?.setAttribute('data-drawer-open', 'false');

    if (parts.checkbox) {
      parts.checkbox.checked = false;
      parts.checkbox.setAttribute('aria-hidden', 'true');
      parts.checkbox.tabIndex = -1;
    }

    syncExpanded(parts.triggers, false);
    attachPanelHandlers(parts);
    drawerState.set(panel.id, { open: false, trigger: drawerState.get(panel.id)?.trigger || null });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initDrawers();
});
